import { useMediaQuery, Grid, Typography, TextField } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { LoadingButton } from '@material-ui/lab';

import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';

import { SectionHeader } from '@/src/components/molecules';
import api from '@/src/lib/api';
import { Yup } from '@/src/lib/yup';

const useStyles = makeStyles(theme => ({
  form: {
    maxWidth: 550,
    margin: `0 auto`,
    '& .MuiInputBase-multiline': {
      background: theme.palette.background.paper,
    },
    '& .MuiOutlinedInput-input': {
      background: theme.palette.background.paper,
    },
    '& .MuiFormHelperText-root': {
      backgroundColor: 'none',
    },
  },
  inputTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(1),
  },
}));

const Form = props => {
  const { className, ...rest } = props;

  const classes = useStyles();
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'), { defaultMatches: true });
  const { enqueueSnackbar } = useSnackbar();

  const formik = useFormik({
    initialValues: {
      phone: '',
      name: '',
      email: '',
      message: '',
    },
    validationSchema: Yup.object({
      name: Yup.string().label('Nome').required('É necessário informar o nome do contato'),
      phone: Yup.string().label('Celular').required('Informe o número do seu celular'),
      email: Yup.string().label('E-mail').email().required('Preencha um e-mail válido'),
      message: Yup.string().label('Mensagem').required('A sua mensagem é necessária'),
    }),
    onSubmit: (values, actions) => {
      api
        .post('/commons/cms/contact/', values)
        .then(() => {
          enqueueSnackbar('Enviamos com sucesso sua mensagem. Aguarde nosso contato.', {
            variant: 'success',
          });
          actions.resetForm();
        })
        .catch(() =>
          enqueueSnackbar('Houve um erro ao processar o envio, tente novamente mais tarde.', {
            variant: 'error',
          }),
        )
        .then(() => {
          actions.setSubmitting(false);
        });
    },
  });

  return (
    <div className={className} {...rest}>
      <SectionHeader
        title="Prefere nos enviar uma mensagem agora?"
        subtitle="Nossa equipe de atendimento está disponível em horário comercial e ficará feliz em ajudar! Aproveite o formulário abaixo e nos envie uma mensagem que retornaremos em breve para você."
        subtitleProps={{
          variant: 'body1',
          color: 'textPrimary',
        }}
        data-aos="fade-up"
        align={isMd ? 'center' : 'left'}
      />
      <form className={classes.form} onSubmit={formik.handleSubmit}>
        <Grid container spacing={isMd ? 4 : 2}>
          <Grid item xs={12} data-aos="fade-up">
            <Typography variant="subtitle1" color="textPrimary" className={classes.inputTitle}>
              Nome completo
            </Typography>
            <TextField
              placeholder="Seu nome e sobrenome"
              variant="outlined"
              size="medium"
              fullWidth
              type="text"
              {...formik.getFieldProps('name')}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />
          </Grid>
          <Grid item xs={12} data-aos="fade-up">
            <Typography variant="subtitle1" color="textPrimary" className={classes.inputTitle}>
              Celular
            </Typography>
            <TextField
              placeholder="Seu telefone celular"
              variant="outlined"
              size="medium"
              fullWidth
              type="tel"
              {...formik.getFieldProps('phone')}
              error={formik.touched.phone && Boolean(formik.errors.phone)}
              helperText={formik.touched.phone && formik.errors.phone}
            />
          </Grid>
          <Grid item xs={12} data-aos="fade-up">
            <Typography variant="subtitle1" color="textPrimary" className={classes.inputTitle}>
              E-mail
            </Typography>
            <TextField
              placeholder="Seu endereço de e-mail, vamos responder através dele"
              variant="outlined"
              size="medium"
              name="email"
              fullWidth
              type="email"
              {...formik.getFieldProps('email')}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />
          </Grid>
          <Grid item xs={12} data-aos="fade-up">
            <Typography variant="subtitle1" color="textPrimary" className={classes.inputTitle}>
              Mensagem
            </Typography>
            <TextField
              placeholder="Detalhe aqui suas dúvidas ou considerações para que possamos ajudar"
              variant="outlined"
              fullWidth
              multiline
              rows={4}
              {...formik.getFieldProps('message')}
              error={formik.touched.message && Boolean(formik.errors.message)}
              helperText={formik.touched.message && formik.errors.message}
            />
          </Grid>
          <Grid item container justifyContent="center" xs={12}>
            <LoadingButton
              loading={formik.isSubmitting}
              variant="contained"
              type="submit"
              color="primary"
              size="large">
              Enviar
            </LoadingButton>
          </Grid>
        </Grid>
      </form>
    </div>
  );
};

Form.propTypes = {
  /**
   * External classes
   */
  className: PropTypes.string,
};

export default Form;
